import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Box, Button, Text } from '../common';
import ScheduleHeader from './ScheduleHeader';
import ScheduleRow from './ScheduleRow';
import CalendarContainer from '@components/Calendar/CalendarContainer';
import useDetectSize from '@hooks/useDetectSize';

const DEFAULT_WEEK_COUNTS = 4;

const MobileOnly = styled(Box)`
  align-self: stretch;
  @media screen and (min-width: 1024px) {
    display: none;
  }
`;

const TextWrapper = styled(Box)`
  display: flex;
  justify-content: flex-start;
  align-items: flex-end;
  width: 92px;
  height: 48px;
  padding: 4px 8px;
  margin-right: 16px;
  @media screen and (min-width: 768px) and (max-width: 1023px) {
    width: 70px;
    justify-content: center;
    align-items: center;
  }
  @media screen and (max-width: 767px) {
    width: 41px;
    margin-right: 14px;
    justify-content: center;
    align-items: center;
  }

  &:last-child {
    margin-right: 0;
  }
`;

const DayRow = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

function ScheduleBoard(props) {
  const {
    specialItems,
    reservedCols,
    disabledExtractor,
    filledExtractor,
    renderSpecialItem,
    renderFooter,
    errorExtractor,
    renderHeader,
    ...restProps
  } = props;
  const { onAdd, onRemove, minRows, maxRows, numOfRows } = restProps;
  const rows = Array(numOfRows || DEFAULT_WEEK_COUNTS).fill(0);
  const isMobile = useDetectSize();

  const days = isMobile
    ? ['M', 'T', 'W', 'T', 'F', 'S']
    : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <Box
      className="schedule-board"
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      flex={1}
      mb={4}
    >
      {renderHeader ? (
        renderHeader()
      ) : (
        <ScheduleHeader title="My Schedule" {...restProps} />
      )}
      <DayRow>
        {days.map((day, index) => {
          const key = `calendar_days_${day}_${index}`;
          return (
            <TextWrapper key={key}>
              <Text fontSize={0}>{day}</Text>
            </TextWrapper>
          );
        })}
      </DayRow>
      <CalendarContainer mb={4}>
        {rows.map((row, index) => {
          const key = `schedule_row_${index}`;

          return (
            <ScheduleRow
              reservedCols={reservedCols}
              filledExtractor={filledExtractor}
              disabledExtractor={disabledExtractor}
              errorExtractor={errorExtractor}
              key={key}
              id={index}
              specialItems={specialItems[`row_${index}`]}
              renderSpecialItem={renderSpecialItem}
            />
          );
        })}
        {renderFooter && renderFooter()}
      </CalendarContainer>
      {(onAdd || onRemove) && (
        <MobileOnly
          mt={2}
          flexDirection="row"
          display="flex"
          alignItems="center"
        >
          <Button
            flex={1}
            variant="secondary"
            disabled={numOfRows <= minRows}
            mr={2}
            onClick={() => {
              if (!onRemove) {
                return;
              }
              onRemove();
            }}
          >
            Remove week
          </Button>
          <Button
            flex={1}
            variant="secondary"
            disabled={maxRows <= numOfRows}
            onClick={() => {
              if (!onAdd) {
                return;
              }
              onAdd();
            }}
          >
            Add week
          </Button>
        </MobileOnly>
      )}
    </Box>
  );
}

ScheduleBoard.propTypes = {
  numOfRows: PropTypes.number,
  specialItems: PropTypes.object,
  renderSpecialItem: PropTypes.func,
  renderHeader: PropTypes.func,
  disabledExtractor: PropTypes.func,
  filledExtractor: PropTypes.func,
  errorExtractor: PropTypes.func,
  renderFooter: PropTypes.func,
  onRemove: PropTypes.func,
  onAdd: PropTypes.func,
  minRows: PropTypes.number,
  maxRows: PropTypes.number,
  reservedCols: PropTypes.array
};

ScheduleBoard.defaultProps = {
  minRows: 3,
  maxRows: 6
};

export default ScheduleBoard;
